import { EMessengerLaunchContext, TMessengerScreen } from '@xometry/ui';
import { omit } from 'es-toolkit/compat';
import { action, computed, observable } from 'mobx';
import FetchableStore from 'stores/shared/FetchableStore';

export enum ESide {
  Right = 'right',
  Left = 'left',
}

export enum ESideOverlayType {
  Messenger = 'messenger',
  None = 'none',
}

/** When set on the messenger overlay, host + MFE render classic without writing layout to localStorage. */
export type TMessengerLayoutPresentationOverride = 'forceClassic';

export interface IMessengerInitialScreenParams {
  initialScreen: TMessengerScreen;
  initialMessageId?: undefined;
}
export interface IMessengerInitialMessageIdParams {
  initialScreen?: undefined;
  initialMessageId: string;
}

export type TSideOverlayContext =
  | ({
      type: ESideOverlayType.Messenger;
      launchContext?: EMessengerLaunchContext;
      /** Classic overlay/column for this session only (nav / deal header); omit to use ErpMessengerLayoutStore. */
      messengerLayoutPresentation?: TMessengerLayoutPresentationOverride;
    } & (IMessengerInitialScreenParams | IMessengerInitialMessageIdParams))
  | {
      type: ESideOverlayType.None;
    };

class SideOverlayStore extends FetchableStore {
  @observable context: TSideOverlayContext = {
    type: ESideOverlayType.None,
  };

  @observable side = ESide.Right;

  @computed get isOpen() {
    return this.context.type !== ESideOverlayType.None;
  }

  @action openSideOverlay = (context: TSideOverlayContext, side: ESide = ESide.Right) => {
    this.side = side;
    this.context = context;
  };

  @action closeSideOverlay = () => {
    this.context = {
      type: ESideOverlayType.None,
    };
    this.side = ESide.Right;
  };

  /** After "Use side panel" from a header-driven classic session — allow compact from persisted store. */
  @action clearMessengerLayoutPresentationOverride = () => {
    if (this.context.type !== ESideOverlayType.Messenger) return;

    if (this.context.messengerLayoutPresentation !== 'forceClassic') return;

    // Drop session-only "force classic"; layout preference stays in ErpMessengerLayoutStore (immutable copy).
    this.context = omit(this.context, ['messengerLayoutPresentation']) as TSideOverlayContext;
  };

  @action patchMessengerOverlay = (
    patch: Partial<Extract<TSideOverlayContext, { type: ESideOverlayType.Messenger }>>,
  ) => {
    if (this.context.type !== ESideOverlayType.Messenger) return;

    this.context = { ...this.context, ...patch } as Extract<TSideOverlayContext, { type: ESideOverlayType.Messenger }>;
  };

  @action toggleSide = () => {
    if (this.side === ESide.Right) {
      this.side = ESide.Left;
    } else {
      this.side = ESide.Right;
    }
  };
}

export default SideOverlayStore;
